<template>
    <div class="p-4">
        <div class="flex items-center mb-4">
            <BackButton to="/settings" />
            <h1 class="text-2xl font-bold">Gerenciar Listas de CNAE</h1>
        </div>

        <Card class="mb-4">
            <template #content>
                <div class="flex justify-between items-center mb-4">
                    <div class="flex items-center">
                        <div class="text-xl font-semibold mr-4">Todas as Listas de CNAE</div>
                        <div class="flex items-center">
                <label for="show-deleted-lists" class="mr-2">Mostrar Listas Excluídas</label>
                <ToggleSwitch id="show-deleted-lists" v-model="showDeletedLists" />
                        </div>
                    </div>
                    <Button label="Criar Nova Lista" @click="showCreateForm = true">
                        <template #icon><span class="material-icons mr-2">add</span></template>
                    </Button>
                </div>

                <DataTable :value="filteredLists" :loading="cnaeListStore.isLoading" responsiveLayout="scroll" emptyMessage="Nenhuma lista de CNAE encontrada." paginator :rows="10">
                    <Column field="name" header="Nome" sortable></Column>
                    <Column v-if="scope === 'account'" header="Tipo" field="is_template" sortable>
                        <template #body="slotProps">
                            <span :class="['p-tag', slotProps.data.is_template ? 'p-tag-info' : 'p-tag-success']">{{ slotProps.data.is_template ? 'Modelo' : 'Personalizada' }}</span>
                        </template>
                    </Column>
                    <Column header="Contagem de CNAE">
                        <template #body="slotProps">
                            {{ slotProps.data.items.filter((item: any) => !item.deleted_at).length }}
                        </template>
                    </Column>
                    <Column header="Criado em" sortable field="inserted_at">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.inserted_at) }}
                        </template>
                    </Column>
                    <Column header="Atualizado em" sortable field="updated_at">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.updated_at) }}
                        </template>
                    </Column>
                    <Column header="Criado por" sortable field="inserted_by.email">
                        <template #body="slotProps">
                            {{ slotProps.data.inserted_by?.name || slotProps.data.inserted_by?.email || 'N/A' }}
                        </template>
                    </Column>
                    <Column v-if="showDeletedLists" header="Excluído em" sortable field="deleted_at">
                        <template #body="slotProps">
                            {{ slotProps.data.deleted_at ? formatDate(slotProps.data.deleted_at) : '-' }}
                        </template>
                    </Column>
                    <Column v-if="showDeletedLists" header="Excluído por" sortable field="deleted_by.email">
                        <template #body="slotProps">
                            {{ slotProps.data.deleted_by?.name || slotProps.data.deleted_by?.email || '-' }}
                        </template>
                    </Column>
                    <Column header="Ações">
                        <template #body="slotProps">
                            <Button class="p-button-rounded p-button-outlined p-button-info mr-2" @click="viewList(slotProps.data)">
                                <template #icon><span class="material-icons">visibility</span></template>
                            </Button>
                            <Button v-if="canEdit(slotProps.data)" class="p-button-rounded p-button-outlined p-button-success mr-2" @click="editList(slotProps.data)">
                                <template #icon><span class="material-icons">edit</span></template>
                            </Button>
                            <Button v-if="canDelete(slotProps.data)" class="p-button-rounded p-button-outlined p-button-danger" @click="confirmDelete(slotProps.data)">
                                <template #icon><span class="material-icons">delete</span></template>
                            </Button>
                        </template>
                    </Column>
                </DataTable>
            </template>
        </Card>

        <Dialog v-model:visible="showCreateForm" header="Criar Lista de CNAE" :modal="true" :closable="false" :draggable="false" class="p-fluid" style="width: 60vw">
            <CnaeListForm @close="showCreateForm = false" :scope="scope" />
        </Dialog>

        <Dialog v-model:visible="showEditForm" header="Editar Lista de CNAE" :modal="true" :closable="false" :draggable="false" class="p-fluid" style="width: 60vw">
            <CnaeListForm :cnaeList="selectedList" @close="showEditForm = false" :scope="scope" />
        </Dialog>

        <Dialog v-model:visible="showViewForm" header="Visualizar Lista de CNAE" :modal="true" :draggable="false" class="p-fluid" style="width: 75vw">
            <CnaeListViewer :cnaeList="selectedList" />
        </Dialog>

         <Dialog v-model:visible="showDeleteConfirmation" header="Confirmar Exclusão" :modal="true" :draggable="false">
            <div class="confirmation-content flex items-center">
                <span class="material-icons text-red-500 text-4xl mr-3">warning</span>
                <span>Tem certeza que deseja excluir <b>{{ selectedList?.name }}</b>?</span>
            </div>
            <template #footer>
                <Button label="Não" class="p-button-text" @click="showDeleteConfirmation = false">
                    <template #icon><span class="material-icons">close</span></template>
                </Button>
                <Button label="Sim" class="p-button-text" @click="deleteSelectedList">
                    <template #icon><span class="material-icons">check</span></template>
                </Button>
            </template>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useSystemCnaeListStore } from '@/stores/systemCnaeList';
import { useAccountCnaeListStore } from '@/stores/accountCnaeList';
import type { CnaeList } from '@/types/cnaeList';
import CnaeListForm from '@/components/CnaeListForm.vue';
import CnaeListViewer from '@/components/CnaeListViewer.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Card from 'primevue/card';
import ToggleSwitch from 'primevue/toggleswitch';
import BackButton from '@/components/base/BackButton.vue';
import { formatDate } from '@/utils/formatters';

const props = defineProps({
    scope: {
        type: String,
        required: true,
        validator: (value: string) => ['system', 'account'].includes(value)
    }
});

const cnaeListStore = props.scope === 'system' ? useSystemCnaeListStore() : useAccountCnaeListStore();

const showCreateForm = ref(false);
const showEditForm = ref(false);
const showViewForm = ref(false);
const showDeleteConfirmation = ref(false);
const selectedList = ref<CnaeList | null>(null);
const showDeletedLists = ref(false);

const filteredLists = computed(() => {
    if (showDeletedLists.value) {
        return cnaeListStore.cnaeLists;
    }
    return cnaeListStore.cnaeLists.filter(list => !list.deleted_at);
});


onMounted(() => {
    cnaeListStore.fetchCnaeLists();
});

const viewList = (list: CnaeList) => {
    selectedList.value = list;
    showViewForm.value = true;
};

const editList = (list: CnaeList) => {
    selectedList.value = list;
    showEditForm.value = true;
};

const confirmDelete = (list: CnaeList) => {
    selectedList.value = list;
    showDeleteConfirmation.value = true;
};

const deleteSelectedList = async () => {
    if (selectedList.value) {
        await cnaeListStore.deleteCnaeList(selectedList.value.id);
    }
    showDeleteConfirmation.value = false;
};

const canEdit = (list: CnaeList) => {
    if (props.scope === 'system') {
        return !list.deleted_at;
    }
    return !list.is_template && !list.deleted_at;
};

const canDelete = (list: CnaeList) => {
    if (props.scope === 'system') {
        return !list.deleted_at;
    }
    return !list.is_template && !list.deleted_at;
};

</script>
